// ═══════════════════════════════════════════════════════════════════════════════
// PROJECT DATA WITH INTERNAL TYPE TAGGING SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════
// 
// Project Types (internal, not visible to users):
// - "social-media"    : Social media grids, Instagram campaigns
// - "branding"        : Brand identity, logo design, visual language
// - "showcase"        : Website mockups, showcase websites
// - "campaign"        : Marketing campaigns, advertising
// - "print"           : Print design, catalogs, magazines
// - "creative"        : Speculative design, art projects
// - "product"         : Product design, packaging
// ═══════════════════════════════════════════════════════════════════════════════

export const PROJECT_TYPES = {
  SOCIAL_MEDIA: "social-media",
  BRANDING: "branding",
  SHOWCASE: "showcase",
  CAMPAIGN: "campaign",
  PRINT: "print",
  CREATIVE: "creative",
  PRODUCT: "product",
  GAME: "game",
};

// Additional grids for Social Media projects (image bank)
export const ADDITIONAL_GRIDS = {
  imtm: [
    {
      title: "Stories & Highlights",
      images: [
        "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/omz06eer_1.png",
        "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/95flj42o_2.png",
        "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/0yq5kpy0_3.png",
        "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/pjf2y7f9_4.png",
        "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/ivphz1y8_5.png",
        "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/88hay3r1_6.png",
      ],
    },
    {
      title: "Event Posters",
      images: [
        "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/xx27dab4_7.png",
        "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/7cka6dho_8.png",
        "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/fmr1wfls_9.png",
      ],
    },
  ],
  dejablue: [
    {
      title: "Cocktail Series",
      images: [
        "https://customer-assets.emergentagent.com/job_visual-works-31/artifacts/ykb9105z_DejaBlue_decgrid_2416_1.png",
        "https://customer-assets.emergentagent.com/job_visual-works-31/artifacts/ykb9105z_DejaBlue_decgrid_2416_1.png",
        "https://customer-assets.emergentagent.com/job_visual-works-31/artifacts/ykb9105z_DejaBlue_decgrid_2416_1.png",
      ],
    },
    {
      title: "Product Shots",
      images: [
        "https://customer-assets.emergentagent.com/job_visual-works-31/artifacts/ykb9105z_DejaBlue_decgrid_2416_1.png",
        "https://customer-assets.emergentagent.com/job_visual-works-31/artifacts/ykb9105z_DejaBlue_decgrid_2416_1.png",
        "https://customer-assets.emergentagent.com/job_visual-works-31/artifacts/ykb9105z_DejaBlue_decgrid_2416_1.png",
      ],
    },
  ],
};

export const PROJECTS = [
  {
    slug: "okd",
    type: PROJECT_TYPES.SHOWCASE,
    title: "OKD Studio",
    subtitle: "Website Design",
    year: "2026",
    role: "Studio Manager & Designer",
    description: "As studio manager and designer, I led the concept, visual language, and design of the studio's website. OKD is a design and production studio that transforms physical spaces into branded environments through strategy, design, manufacturing, and installation.",
    description2: "The project focused on creating a clear and elegant digital presence that presents the studio's projects, services, and design process.",
    next: "balsan",
  },
  {
    slug: "balsan",
    type: PROJECT_TYPES.SOCIAL_MEDIA,
    title: "Balsan",
    subtitle: "Social Media Grid",
    year: "2026",
    role: "Art Direction, Visual Design",
    client: "Balsan",
    description: "Balsan is a French brand specializing in high-quality carpet and textile flooring solutions for commercial and public spaces.",
    description2: "I created a structured social media visual language highlighting textures, color palettes, and spatial applications, building a cohesive and elegant grid for the brand's digital presence. The system balances product detail with spatial inspiration, allowing materials, patterns, and collections to be presented in a clear and visually engaging way. It establishes a consistent rhythm across the feed while emphasizing the tactile and architectural qualities of the brand's flooring designs.",
    galleryComponent: "BalsanGallery",
    next: "smoovee",
  },
  {
    slug: "smoovee",
    type: PROJECT_TYPES.SOCIAL_MEDIA,
    title: "Smoovee",
    subtitle: "Social Media Grid",
    year: "2026",
    role: "Art Director",
    client: "Smoovee - Kitchen Mobility Surfaces Startup",
    description: "Art direction and design of a social media grid for Smoovee, a startup developing mobile surfaces that allow kitchen appliances to move easily across the countertop.",
    description2: "The project focused on creating a clear visual language combining clean product imagery with everyday kitchen scenarios to demonstrate the product's functionality and ease of use. Through bold typography and short messaging, the grid highlights familiar kitchen moments and presents Smoovee as a simple, smart solution for improving the way appliances are used in the kitchen.",
    galleryComponent: "SmooveeGallery",
    next: "ceremonie",
  },
  {
    slug: "ceremonie",
    type: PROJECT_TYPES.SOCIAL_MEDIA,
    title: "Cérémonie Tea",
    subtitle: "Social Media Grid",
    year: "2026",
    role: "Art Direction, Visual Design",
    client: "Cérémonie Tea",
    description: "Cérémonie Tea is an Israeli premium tea brand founded in 2003, known for whole-leaf teas and elegant packaging inspired by traditional tea rituals.",
    description2: "I designed a cohesive social media grid language that blends product storytelling, seasonal themes, and sensory imagery. The visual system highlights the ritual and atmosphere of tea drinking through warm lighting, natural textures, and refined compositions. The grid creates a calm, premium rhythm while allowing different collections, flavors, and moments of tea culture to unfold visually across the feed.",
    galleryComponent: "CeremonieGallery",
    next: "worx",
  },
  {
    slug: "worx",
    type: PROJECT_TYPES.SOCIAL_MEDIA,
    title: "Worx Design",
    subtitle: "Social Media Grid",
    year: "2026",
    role: "Art Direction, Visual Design",
    client: "Worx Design",
    description: "Worx Design is an Israeli company specializing in architectural solutions, furniture, and acoustic products for offices and public spaces.",
    description2: "As Art Director and Designer, I developed a cohesive visual language for the brand's social media grid. The system combines clean product imagery, architectural compositions, and modern typography to create a structured and consistent brand presence across digital platforms.",
    galleryComponent: "WorxGallery",
    next: "dejablue",
  },
  {
    slug: "dejablue",
    type: PROJECT_TYPES.SOCIAL_MEDIA,
    title: "Deja Blue",
    subtitle: "Social Media Grid",
    year: "2025-2026",
    role: "Art Director - Visual Language",
    client: "Deja Blue - Liqueurs & Spirits Distribution",
    description: "Development and art direction of the visual language for the brand's social media grid. Creation of a bold and colorful post system highlighting flavors, cocktails, and mixology products while building a strong and cohesive digital presence.",
    description2: "The design combines strong product-centered compositions, vibrant color palettes, and fruit and splash elements to create a fresh and dynamic visual experience.",
    galleryComponent: "DejaBlueGallery",
    next: "imtm",
  },
  {
    slug: "imtm",
    type: PROJECT_TYPES.SOCIAL_MEDIA,
    title: "IMTM 2026",
    subtitle: "Social Media Grid",
    year: "2026",
    role: "Studio Manager & Designer",
    client: "IMTM - International Mediterranean Tourism Market",
    description: "Art direction and design of a social media grid for IMTM, Israel's leading international tourism exhibition. The visual language combines bold typography, a vibrant color palette, and destination imagery to communicate global connectivity and cultural diversity.",
    description2: "The project strengthens the event's digital presence through a flexible and cohesive social media system, creating a consistent visual identity across all platforms.",
    galleryComponent: "IMTMGallery",
    next: "truestory-fullfunnel",
  },
  {
    slug: "truestory-fullfunnel",
    type: PROJECT_TYPES.CAMPAIGN,
    title: "True Story",
    subtitle: "Full-Funnel Performance Campaign",
    year: "2026",
    role: "Art and Creative Direction",
    client: "True Story Israel",
    description: "True Story is an Israeli wellness and beauty brand specializing in collagen-based supplements designed to support healthier skin, hair, nails, and overall well-being.",
    description2: "For this full-funnel performance campaign, I developed a series of creative directions tailored to the different stages of the customer journey - awareness, consideration, and conversion. Each stage used a distinct visual and messaging approach, from attention-grabbing conceptual visuals and lifestyle storytelling to clear product benefits and direct calls to action. The creative system translates functional product advantages into simple and memorable visual stories, combining product-focused compositions, ingredient highlights, and playful metaphors. Together, the creatives form a flexible performance framework designed to capture attention, communicate value quickly, and drive engagement and conversions across social media advertising.",
    isMultiSlider: true,
    sliders: [
      {
        label: "Awareness Stage",
        images: [
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/qtxkg3bb_1.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/7qpxd9sp_Post_true%20story_Campagin%20rmaketing%203_2471_2.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/i8s9t621_Post_true%20story_Campagin%20rmaketing%203_2471_4.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/cnnrbokf_Post_true%20story_Campagin%20rmaketing%203_2471_5.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/ez6ab46q_Post_true%20story_Campagin%20rmaketing%203_2471_7.png",
        ],
      },
      {
        label: "Consideration Stage",
        images: [
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/qon5p34p_1.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/19z9j6ay_Post_true_remarketingCam_2603_1.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/8u12lgwi_Post_true_remarketingCam_2603_2.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/iunvgjvp_Post_true_remarketingCam_2603_3.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/lxng26bh_Post_true_remarketingCam_2603_4.png",
        ],
      },
      {
        label: "Conversion Stage",
        images: [
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/sy6je7ez_post_true%20story_30%25camp_2804_3.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/53v63798_post_true%20story_30%25camp_2804_5.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/9kyf7b7h_Post_true%20story_Campagin%2030p_2804_1.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/w5dkjne3_Post_true%20story_Campagin%2030p_2804_2.png",
        ],
      },
    ],
    images: [
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/qtxkg3bb_1.png",
    ],
    next: "goodlife-ab",
  },
  {
    slug: "goodlife-ab",
    type: PROJECT_TYPES.CAMPAIGN,
    title: "Goodlife Outdoors",
    subtitle: "A/B Testing Campaign",
    year: "2026",
    role: "Art Direction, Performance Campaign Design",
    client: "Goodlife Outdoors",
    description: "Goodlife Outdoors is a lifestyle brand specializing in premium outdoor products such as hot tubs, outdoor showers, bean bags, and hosting solutions designed for gardens, balconies, and open-air living.",
    description2: "For this performance campaign, I developed multiple creative directions for A/B testing across social media ads. Each concept explored a different communication angle - product-focused visuals, lifestyle scenarios, and minimal feature-driven compositions - allowing the campaign to test messaging, visual hierarchy, and emotional triggers. The system was designed to quickly identify high-performing creatives while maintaining a clean and cohesive brand language aligned with the outdoor lifestyle the brand promotes.",
    isABTest: true,
    imagesA: [
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/s7zqwlk4_v1.png",
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/dbhgrazb_v1-1.png",
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/jkky0g7s_v2.png",
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/bwjg21do_v2-1.png",
    ],
    imagesB: [
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/cy2uqjsi_v1-2.png",
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/xeukdy1b_v1-3.png",
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/efdp5tu0_v2-2.png",
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/mrhig5au_v3.png",
    ],
    images: [
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/l2zz6cai_1.png",
    ],
    next: "bob",
  },
  {
    slug: "bob",
    type: PROJECT_TYPES.BRANDING,
    title: "Bob",
    subtitle: "Brand Concept",
    year: "2018",
    role: "Student",
    client: "Bezalel Academy of Arts and Design - Branding Course",
    introText: "The concept centers around Bob, the modest owner of a small apartment hotel who offers simple, no-frills hospitality while counting the days until retirement.",
    description: "A branding project developed during my studies at Bezalel Academy, exploring character-driven storytelling as a foundation for brand identity.",
    description2: "The project translates this narrative into a visual language that reflects honesty, simplicity, and understated charm.",
    galleryComponent: "BobGallery",
    next: "polygon",
  },
  {
    slug: "polygon",
    type: PROJECT_TYPES.CREATIVE,
    title: "Spring Catalog",
    subtitle: "Facial Privacy Accessory",
    year: "2018",
    role: "Student",
    client: "Bezalel Academy of Arts and Design - Speculative Design Course",
    introText: "The project proposes a wearable accessory that alters facial geometry to disrupt recognition systems.",
    description: "A senior-year speculative design project exploring how individuals might protect their identity in a world of constant surveillance and facial recognition.",
    description2: "Using face scans and generative design in Grasshopper, custom petal-shaped polygons were created for each face and presented through a spring-inspired fashion catalog, framing the concept as a desirable and ready-to-use product.",
    galleryComponent: "PolygonGallery",
    next: "flormar",
  },
  {
    slug: "flormar",
    type: PROJECT_TYPES.CAMPAIGN,
    title: "Flormar",
    subtitle: "Creative Campaign Concept",
    year: "2026",
    role: "Art Direction, Creative Concept, Campaign Design",
    client: "Flormar",
    description: "Flormar is an international beauty brand focused on color, creativity, and self-expression through makeup.",
    description2: "For this campaign, I developed a creative visual concept built around the idea of makeup as part of everyday life - not just decoration but a tool for expression and mood. The creative system combines bold color palettes, clean typography, and playful storytelling directions that connect beauty products to daily routines, seasonal moods, and emotional experiences. The campaign was designed as a flexible storytelling system, allowing different creative scenarios and visual narratives while maintaining a consistent brand language.",
    isMultiSlider: true,
    sliders: [
      {
        label: "Audience A - Bold & Playful",
        images: [
          "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/o7oq15jv_Instagram%20post%20-%2017.png",
          "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/9ag8zf7h_Instagram%20post%20-%2018.png",
          "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/lxoy0psb_Instagram%20post%20-%2019.png",
          "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/i1wnsgeo_Instagram%20post%20-%2020.png",
          "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/vb7opmhe_Instagram%20post%20-%2022.png",
        ],
      },
      {
        label: "Audience B - Soft & Natural",
        images: [
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/pyq5axko_Instagram%20post%20-%2010.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/zha65vxn_Instagram%20post%20-%2011.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/vxhlyqxr_Instagram%20post%20-%2012.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/wrzhu61f_Instagram%20post%20-%2013.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/pnrzb2we_Instagram%20post%20-%2014.png",
        ],
      },
      {
        label: "Audience C - Mediterranean Edition",
        images: [
          "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/r4ndbypm_Instagram%20post%20-%201.png",
          "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/y5uh0nok_Instagram%20post%20-%202.png",
          "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/pgdlla4v_Instagram%20post%20-%203.png",
          "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/k5kzm9op_Instagram%20post%20-%204.png",
          "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/lgf2h9m9_Instagram%20post%20-%205.png",
        ],
      },
      {
        label: "Audience D - Sugar Plum Collection",
        images: [
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/wq9vzlve_Instagram%20post%20-%2024.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/ci9vys18_Instagram%20post%20-%2025.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/jcvu91gl_Instagram%20post%20-%2026.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/8r79vw11_Instagram%20post%20-%2027.png",
          "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/g0m34h4m_Instagram%20post%20-%2028.png",
        ],
      },
    ],
    images: [
      "https://customer-assets.emergentagent.com/job_artful-avoidance/artifacts/o7oq15jv_Instagram%20post%20-%2017.png",
    ],
    next: "theplane",
  },
  {
    slug: "theplane",
    type: PROJECT_TYPES.BRANDING,
    title: "The Plane",
    subtitle: "Aircraft Livery Design",
    year: "2023",
    role: "Concept Design, Designer",
    client: "Israel Aerospace Industries (IAI)",
    description: "In 2023, IAI competed to supply a Gulfstream G280 mission aircraft to the Finnish military. As part of the proposal, I designed the aircraft's exterior according to the Finnish Navy's visual language.",
    description2: "Inspired by the Navy and Coast Guard identity, the design uses bold diagonals, minimal color, and the signature navy blue. The final concept was approved for production and developed in collaboration with a Finnish print shop to create the painting stencils.",
    mainImages: [
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/e5hoojyb_extrior2.png",
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/h35tyyzm_extrior6.png",
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/0zwrvfhs_extrior8.png",
    ],
    secondaryImages: [
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/50zclphc_news-10.jpg",
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/s8fqf82c_Screen%20Shot%202024-12-15%20at%2017.52.52.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/voap1xis_coastGuard.jpg",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/e0yn9qfu_Asset%201.png",
    ],
    images: [
      "https://customer-assets.emergentagent.com/job_design-works-16/artifacts/e5hoojyb_extrior2.png",
    ],
    next: "poster",
  },
  {
    slug: "poster",
    type: PROJECT_TYPES.PRINT,
    title: "Poster",
    subtitle: "Print Design",
    year: "2024",
    role: "Design",
    description: "A collection of marketing posters created for events, cultural initiatives, and brands. Each poster explores a distinct visual concept, combining bold typography, expressive imagery, and clear messaging to capture attention and communicate the core idea quickly and effectively.",
    images: [
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/fu78kt5h_resizing.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/catkzoa8_0fc2c8d9-2812-480d-a362-88b94b6a738d.JPG",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/let7j63l_resizingai.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/9scyayuz_web.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/xx8vz6fh_bob-03.png",
    ],
    next: "moments",
  },
  {
    slug: "moments",
    type: PROJECT_TYPES.BRANDING,
    title: "Moments",
    subtitle: "Brand Design",
    year: "2024",
    role: "Design",
    client: "Moments Studio",
    description: "When I joined the company, it had an outdated logo and no consistent branding materials. I initiated a complete redesign, creating a new logo and a cohesive visual identity for the studio.",
    description2: "The new branding reflects the company's character - a young, agile studio working in the defense industry, combining technological precision with creativity. The identity was expanded into a full visual system including icons, graphic elements, marketing materials, and website design, creating a clear and confident brand presence.",
    heroImage: "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/jdwr4hm7_Screen%20Shot%202024-12-30%20at%2016.04.45.png",
    sketches: [
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/82cjvd69_Screen%20Shot%202024-12-30%20at%2016.46.46.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/7pepsyen_Screen%20Shot%202024-12-30%20at%2016.47.49.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/95flhtxb_Screen%20Shot%202024-12-30%20at%2016.50.11.png",
    ],
    outputs: [
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/8wxoeego_Screen%20Shot%202024-12-30%20at%2017.15.01.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/y5cpmm3r_Screen%20Shot%202024-12-30%20at%2017.15.19.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/wat3xnyw_Screen%20Shot%202024-12-30%20at%2017.27.23.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/vt6jmkiy_Screen%20Shot%202024-12-30%20at%2017.30.26.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/hltduz5n_Screen%20Shot%202024-12-30%20at%2017.30.41.png",
    ],
    mockups: [
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/vlrw9sit_brochure.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/e214qhvt_BUSINESS%20CARD%20WIREFRAME.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/cqkuakku_nobg.png",
      "https://customer-assets.emergentagent.com/job_minimal-portfolio-87/artifacts/h90rtu4r_Spiral%20Notebook%20.png",
    ],
    next: "valhalla",
  },
  {
    slug: "valhalla",
    type: PROJECT_TYPES.GAME,
    title: "Valhalla",
    subtitle: "Viking Naval Game",
    year: "2018",
    role: "Game Design",
    client: "Bezalel Academy of Arts and Design",
    description: "A gameplay concept developed during my studies, inspired by Viking ships and Norse mythology. In the game Valhalla, players battle by maneuvering their ships and crashing into the opponent to sink them. Throughout the match, players can collect Viking-themed charms such as wind and speed boosts that help them gain advantage and control the battle. The mechanics create fast, physical naval combat inspired by the spirit of Viking raids.",
    videoUrl: "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/cgmq25ip_Valhalla%20-%20GamePlay.mp4",
    images: [
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/msgtvuhw_Valhalla%20-%20GamePlay.00_00_02_21.Still001.png",
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/nwoj2xux_0.png",
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/mljp82sd_1.png",
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/8kvxy4x6_2.png",
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/d619t805_4.png",
    ],
    next: "narwal",
  },
  {
    slug: "narwal",
    type: PROJECT_TYPES.CAMPAIGN,
    title: "Narwal",
    subtitle: "Passover Campaign",
    year: "2026",
    role: "Art Director",
    client: "Narwal",
    description: "Art direction for a Passover campaign promoting Narwal's cleaning product line. The visual concept connects the idea of spring cleaning with iconic Passover imagery, using dramatic environments inspired by the Red Sea and ancient Egypt. The campaign combines strong product-focused compositions, bold typography, and promotional messaging to create a striking social and performance-driven advertising series for multiple product models.",
    images: [
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/uqhtegrp_1.png",
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/1rzuf2zr_flow_product_v2_1080x1920.png",
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/lo31405a_s30_lifestyle_v1_1080x1920.png",
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/grdczmjw_s30pro_lifestyle_v1_1080x1920.png",
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/jsvqydef_s30pro_product_v1_1080x1920.png",
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/8zdr5g87_z10_lifestyle_v1_1080x1920.png",
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/7afjrcb1_z10ultra_lifestyle_v2_1080x1920.png",
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/bz15x7iw_z10ultra_product_v1_1080x1920.png",
      "https://customer-assets.emergentagent.com/job_purple-portfolio-16/artifacts/d3vrn3zo_z10ultra_product_v2_1080x1920.png",
    ],
    next: "monox",
  },
  {
    slug: "monox",
    type: PROJECT_TYPES.BRANDING,
    title: "Monox+",
    subtitle: "Premium Launch",
    year: "2026",
    role: "Art Director",
    client: "Monox",
    description: "Monox+ is not just an upgrade - it's a shift in attitude.\n\nA premium layer designed to feel faster, sharper and more in control, both visually and conceptually. The challenge was to push the brand forward without breaking it - to create a system that feels like the same world, just with more edge, more confidence and more presence.",
    description2: "From the + mark to a full launch ecosystem, the project expands across campaign, merchandise and physical experience - turning a feature into a statement.",
    logos: [
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/7llgkz6m_Group%2040.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/qw6crerl_ChatGPT%20Image%20May%202%2C%202026%2C%2001_46_57%20PM.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/qhtv5t1j_ChatGPT%20Image%20May%202%2C%202026%2C%2002_05_29%20PM.png",
    ],
    posts: [
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/oiff8ruu_monox_marchgrid_TSK-2777_po4_2.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/cwcd62sj_monox_marchgrid_TSK-2777_po5.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/0ydza08g_monox_marchgrid_TSK-2777_po4.png",
    ],
    merchandise: [
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/66c18f0v_ChatGPT%20Image%20May%202%2C%202026%2C%2002_30_27%20PM.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/56chd2rk_ChatGPT%20Image%20May%202%2C%202026%2C%2002_41_04%20PM.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/72z0a8v1_ChatGPT%20Image%20May%202%2C%202026%2C%2002_48_54%20PM.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/c8d5p0ou_ChatGPT%20Image%20May%202%2C%202026%2C%2002_55_39%20PM.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/udeg9v24_ChatGPT%20Image%20May%202%2C%202026%2C%2002_44_26%20PM.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/fz8i74q8_ChatGPT%20Image%20May%202%2C%202026%2C%2002_42_06%20PM.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/6mx3u4q0_ChatGPT%20Image%20May%202%2C%202026%2C%2002_58_23%20PM.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/qobrxb9u_ChatGPT%20Image%20May%202%2C%202026%2C%2003_05_37%20PM.png",
    ],
    launchWall: [],
    next: "talks",
  },
  {
    slug: "talks",
    type: PROJECT_TYPES.BRANDING,
    title: "Talks",
    subtitle: "Podcast Recording Service",
    year: "2026",
    role: "Art Director & Brand Designer",
    client: "THM",
    accentColor: "#8B8BF5",
    description: "Talks by THM is a podcast recording service built around one idea: if the conversation matters - it should look and sound like it.\n\nI led the entire creative, from concept to execution - naming, brand, logo, visual language, icons and social - all while staying anchored to THM's existing logo and brand DNA.\n\nThe result is a system that turns raw conversations into a clear, confident presence.",
    originalLogo: "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/d9m9ghw2_Full%20logo%20Blue.png",
    logoSketches: [
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/y5y37oxq_Screenshot%202026-05-02%20at%2015.18.42.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/muj8udi0_Screenshot%202026-05-02%20at%2015.18.50.png",
    ],
    selectedLogo: "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/x2j3nl3j_logo_black_orange_slogan.png",
    selectedLogoVariants: [
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/zh3dohcl_logo_orange_thm.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/vwd6yfkw_logo_round_orange.png",
    ],
    typography: [
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/f8k85ox6_1.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/9eqg96bl_Instagram%20post%20-%2013.png",
      "https://customer-assets.emergentagent.com/job_8f19abab-0179-4d97-b1c9-811ecc7078a8/artifacts/dg054dhb_3.png",
    ],
    colors: null,
    fontsAndColors: [],
    icons: [
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/k7gcqj6z_1.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/wb5zvl9s_2.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/sq5t6k6h_3.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/a091b3cb_4.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/pc2itjcd_5.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/ey430th2_6.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/863dc4xq_7.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/0arnxyyn_Group%2058.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/m8oxyglw_Group-1.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/bxul1m9a_Group.png",
    ],
    merchAndPosts: [
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/43nqrxwl_ChatGPT%20Image%20May%202%2C%202026%2C%2004_02_00%20PM.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/ptxbn8z9_Instagram%20post%20-%2011.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/9w2l5wcy_Instagram%20post%20-%2012.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/rd4mrwbg_Instagram%20post%20-%2014.png",
      "https://customer-assets.emergentagent.com/job_9f406166-4b82-40e8-babc-973628d6509c/artifacts/76c2laek_Instagram%20post%20-%2015.png",
    ],
    next: "balsan",
  },
];

// Get project by slug
export const getProjectBySlug = (slug) => {
  return PROJECTS.find(p => p.slug === slug?.toLowerCase());
};

// Get next project
export const getNextProject = (currentSlug) => {
  const current = getProjectBySlug(currentSlug);
  if (!current) return PROJECTS[0];
  return getProjectBySlug(current.next) || PROJECTS[0];
};

// Get projects by type
export const getProjectsByType = (type) => {
  return PROJECTS.filter(p => p.type === type);
};

// Get additional grids for a project
export const getAdditionalGrids = (slug) => {
  return ADDITIONAL_GRIDS[slug] || [];
};
